/**
 * Share a workflow (AMO-170): its owner chooses what everyone in the
 * organization may do with it (nothing, run it, or edit it) and which
 * members it is shared with, each to run or to edit. Everyone it is shared
 * with sees only their own runs; the owner sees every run.
 *
 * @module components/workflows/WorkflowShareDialog
 */
import { WORKFLOW_LIMITS, type WorkflowShareRole, type WorkflowSharing } from "@amoreg/contracts";
import { PlusIcon, Share2Icon, XIcon } from "lucide-react";
import { useEffect, useState } from "react";

import { setWorkflowSharing } from "~/state/workflows";
import { Button } from "../ui/button";
import { Dialog, DialogClose, DialogDescription, DialogFooter, DialogHeader, DialogPanel, DialogPopup, DialogTitle } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger } from "../ui/select";
import type { MemberChoice } from "./PeoplePicker";
import { shareableMembers } from "./WorkflowShareDialog.logic";

const EVERYONE_CHOICES = [
  { value: "none", label: "Only the people below" },
  { value: "run", label: "Everyone in the organization can run it" },
  { value: "edit", label: "Everyone in the organization can edit it" },
] as const;

const ROLE_LABEL: Readonly<Record<WorkflowShareRole, string>> = { run: "Can run", edit: "Can edit" };

export function WorkflowShareDialog({
  open,
  onOpenChange,
  organizationId,
  workflowId,
  sharing,
  members,
  ownerEmail,
}: {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly organizationId: string;
  readonly workflowId: string;
  readonly sharing: WorkflowSharing;
  /** The organization's members; null when they cannot be read. */
  readonly members: ReadonlyArray<MemberChoice> | null;
  readonly ownerEmail: string | null;
}) {
  const [draft, setDraft] = useState<WorkflowSharing>(sharing);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Each opening starts from what is saved now.
  useEffect(() => {
    if (!open) return;
    setDraft(sharing);
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only on opening
  }, [open]);

  const addable = shareableMembers(members ?? [], draft.people, ownerEmail);
  const nameOf = (email: string) => members?.find((member) => member.email.toLowerCase() === email.toLowerCase())?.name ?? null;
  const full = draft.people.length >= WORKFLOW_LIMITS.sharedPeople;

  const save = async () => {
    setBusy(true);
    setError(null);
    const outcome = await setWorkflowSharing(organizationId, workflowId, draft);
    setBusy(false);
    if (!outcome.ok) {
      setError(outcome.message);
      return;
    }
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={(next) => (!busy ? onOpenChange(next) : undefined)}>
      <DialogPopup className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Share</DialogTitle>
          <DialogDescription>
            People you share this workflow with can run it, or edit it too. Each of them sees only their own runs; you
            see every run. Only you can share or delete it.
          </DialogDescription>
        </DialogHeader>
        <DialogPanel className="space-y-3" data-workflow-share-dialog="">
          <label className="block space-y-1.5">
            <span className="text-xs font-medium">Everyone in the organization</span>
            <Select
              value={draft.everyone ?? "none"}
              onValueChange={(value) => setDraft({ ...draft, everyone: value === "run" || value === "edit" ? value : null })}
            >
              <SelectTrigger size="sm" aria-label="Everyone in the organization" data-workflow-share-everyone="">
                <span className="truncate text-xs">{EVERYONE_CHOICES.find((choice) => choice.value === (draft.everyone ?? "none"))?.label}</span>
              </SelectTrigger>
              <SelectContent>
                {EVERYONE_CHOICES.map((choice) => (
                  <SelectItem key={choice.value} value={choice.value}>
                    {choice.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>
          <div className="space-y-1.5">
            <span className="text-xs font-medium">People</span>
            {draft.people.length === 0 ? <p className="text-[12px] text-muted-foreground">Nobody yet.</p> : null}
            <ul className="space-y-1">
              {draft.people.map((person) => (
                <li key={person.email} className="flex items-center gap-2 rounded-md bg-muted/40 px-2 py-1 text-[12.5px]" data-workflow-share-person={person.email}>
                  <span className="min-w-0 flex-1 truncate">
                    {nameOf(person.email) ? (
                      <>
                        {nameOf(person.email)} <span className="text-muted-foreground">{person.email}</span>
                      </>
                    ) : (
                      person.email
                    )}
                  </span>
                  <Select
                    value={person.role}
                    onValueChange={(value) =>
                      setDraft({
                        ...draft,
                        people: draft.people.map((entry) => (entry.email === person.email ? { ...entry, role: value === "edit" ? "edit" : "run" } : entry)),
                      })
                    }
                  >
                    <SelectTrigger size="sm" className="w-28" aria-label={`Role for ${person.email}`} data-workflow-share-role={person.email}>
                      <span className="text-xs">{ROLE_LABEL[person.role]}</span>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="run">{ROLE_LABEL.run}</SelectItem>
                      <SelectItem value="edit">{ROLE_LABEL.edit}</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    size="icon-xs"
                    variant="ghost"
                    aria-label={`Stop sharing with ${person.email}`}
                    onClick={() => setDraft({ ...draft, people: draft.people.filter((entry) => entry.email !== person.email) })}
                  >
                    <XIcon />
                  </Button>
                </li>
              ))}
            </ul>
            {members !== null && addable.length > 0 && !full ? (
              <Select
                value=""
                onValueChange={(value) =>
                  typeof value === "string" && value ? setDraft({ ...draft, people: [...draft.people, { email: value.toLowerCase(), role: "run" }] }) : undefined
                }
              >
                <SelectTrigger size="sm" aria-label="Share with a member" data-workflow-share-add="">
                  <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <PlusIcon className="size-3.5" />
                    Add a member
                  </span>
                </SelectTrigger>
                <SelectContent>
                  {addable.map((member) => (
                    <SelectItem key={member.email} value={member.email}>
                      {member.name ? `${member.name} · ${member.email}` : member.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : null}
            {members === null ? <p className="text-[11px] text-muted-foreground">The organization's members could not be read just now.</p> : null}
          </div>
          {error ? <p className="text-sm text-destructive">{error}</p> : null}
        </DialogPanel>
        <DialogFooter>
          <DialogClose render={<Button size="sm" variant="ghost" disabled={busy}>Cancel</Button>} />
          <Button size="sm" onClick={() => void save()} disabled={busy} data-workflow-share-save="">
            <Share2Icon />
            {busy ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogPopup>
    </Dialog>
  );
}
